import { z } from "zod";

export const contactTopics = [
  "technical_support",
  "billing",
  "sales",
  "partnership",
  "general",
] as const;

export type ContactTopic = (typeof contactTopics)[number];

export const contactTopicLabels: Record<ContactTopic, string> = {
  technical_support: "Technical support",
  billing: "Billing & account",
  sales: "Sales & plans",
  partnership: "Partnership",
  general: "General question",
};

export const contactFormSchema = z.object({
  name: z.string().trim().min(2, "Please enter your name.").max(100),
  email: z
    .string()
    .trim()
    .email("Please enter a valid email address.")
    .max(254),
  topic: z.enum(contactTopics),
  subject: z.string().trim().min(3, "Please add a short subject.").max(160),
  message: z
    .string()
    .trim()
    .min(20, "Please include a little more detail so we can help.")
    .max(5_000, "Please keep your message under 5,000 characters."),
  turnstileToken: z.string().min(1, "Please complete the security check."),
  website: z.string().max(200),
});

export type ContactFormValues = z.infer<typeof contactFormSchema>;

export function escapeContactHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export function createContactEmail(input: {
  id: string;
  name: string;
  email: string;
  topic: ContactTopic;
  subject: string;
  message: string;
  createdAt: Date;
}) {
  const topicLabel = contactTopicLabels[input.topic];
  const escapedMessage = escapeContactHtml(input.message).replaceAll(
    "\n",
    "<br />",
  );
  const subject = `[PdfParse contact: ${topicLabel}] ${input.subject.replace(/[\r\n]+/g, " ")}`;

  return {
    subject,
    text: [
      "New PdfParse contact request",
      "",
      `Topic: ${topicLabel}`,
      `Name: ${input.name}`,
      `Email: ${input.email}`,
      `Subject: ${input.subject}`,
      `Submitted: ${input.createdAt.toISOString()}`,
      `Submission ID: ${input.id}`,
      "",
      input.message,
    ].join("\n"),
    html: `
      <div style="background:#f4f4f5;padding:32px 16px;font-family:Arial,sans-serif;color:#18181b">
        <div style="max-width:640px;margin:0 auto;background:#ffffff;border:1px solid #e4e4e7">
          <div style="padding:24px 28px;border-bottom:1px solid #e4e4e7">
            <div style="font-size:12px;letter-spacing:.12em;text-transform:uppercase;color:#71717a">PdfParse contact</div>
            <h1 style="margin:8px 0 0;font-size:22px;line-height:1.3">${escapeContactHtml(input.subject)}</h1>
          </div>
          <div style="padding:24px 28px">
            <table role="presentation" style="width:100%;border-collapse:collapse;font-size:14px">
              <tr><td style="padding:6px 0;color:#71717a;width:110px">Topic</td><td style="padding:6px 0">${escapeContactHtml(topicLabel)}</td></tr>
              <tr><td style="padding:6px 0;color:#71717a">From</td><td style="padding:6px 0">${escapeContactHtml(input.name)} &lt;${escapeContactHtml(input.email)}&gt;</td></tr>
              <tr><td style="padding:6px 0;color:#71717a">Submitted</td><td style="padding:6px 0">${escapeContactHtml(input.createdAt.toISOString())}</td></tr>
              <tr><td style="padding:6px 0;color:#71717a">ID</td><td style="padding:6px 0;font-family:monospace">${escapeContactHtml(input.id)}</td></tr>
            </table>
            <div style="margin-top:22px;padding:18px;background:#fafafa;border-left:3px solid #18181b;font-size:15px;line-height:1.65">${escapedMessage}</div>
          </div>
          <div style="padding:16px 28px;border-top:1px solid #e4e4e7;color:#71717a;font-size:12px">Reply to this email to respond directly to ${escapeContactHtml(input.name)}.</div>
        </div>
      </div>`,
  };
}
